import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { selectIsMobile } from 'redux/modules/device';
import { isAbsent, isEmpty, isPresent } from 'helpers/verify';
import css from './CardSelection.scss';

/**
 * The CardSelection component implements common logic for selecting from a collection of Cards
 * on desktop and mobile.
 *
 * CardSelection accepts two types of children: an array of render functions, or an object mapping
 * keys to render functions. The render function should return a node when invoked.
 * Invalid React elements will be filtered out. The renderFunction is invoked with:
 *
 *   renderFunction({
 *     handleSelection: function, // should be invoked when the card is selected (usually onClick).
 *     selected: boolean,         // true if the card was selected most recently, false otherwise.
 *   })
 *
 * On desktop, calling `handleSelection` invokes `onCardSelected` with the key of the card.
 *
 * On mobile, a CTA is rendered. `handleSelection` invocations persist the key of the card in state,
 * flagging the card as selected. When the CTA is clicked, `onCardSelected` is invoked with the key of
 * the flagged card.
 *
 *
 * Example:
 *
 * Object with render functions:
 *
 *   <CardSelection
 *     onCardSelected={key => console.log(key)} // "foo" or "bar"
 *   >
 *     {{
 *       foo: ({ selected, handleSelection }) => <Card onClick={handleSelection} selected={selected} />,
 *       bar: ({ selected, handleSelection }) => <Card onClick={handleSelection} selected={selected} />
 *     }}
 *   </CardSelection>
 *
 * Array with render functions:
 *
 *   <CardSelection
 *     onCardSelected={key => console.log(key)} // 0 or 1
 *   >
 *     {[
 *       ({ selected, handleSelection }) => <Card onClick={handleSelection} selected={selected} />,
 *       ({ selected, handleSelection }) => <Card onClick={handleSelection} selected={selected} />
 *     ]}
 *   </CardSelection>
 */

export const mapStateToProps = state => ({
  isMobile: selectIsMobile(state),
});

export class CardSelectionUnwrapped extends Component {
  constructor(props) {
    super(props);
    this.handleCtaClick = this.handleCtaClick.bind(this);
  }

  state = {
    selected: this.props.defaultSelected || null,
  };

  handleSelection(key) {
    const { isMobile, onCardSelected } = this.props;

    if (isMobile) {
      this.setState({ selected: key });
    } else {
      onCardSelected(key);
    }
  }

  handleCtaClick() {
    const { selected } = this.state;

    if (isPresent(selected)) {
      this.props.onCardSelected(selected);
    }
  }

  renderCards() {
    const { children } = this.props;

    return Object.keys(children || [])
      .map(key =>
        children[key]({
          handleSelection: this.handleSelection.bind(this, key),
          selected: this.state.selected === key,
        })
      )
      .filter(React.isValidElement);
  }

  render() {
    const { isMobile, mobileCtaCopy } = this.props;
    const renderedCards = this.renderCards();

    return (
      !isEmpty(renderedCards) && (
        <div className={css.base}>
          <div className={css.cards}>{renderedCards}</div>
          {isMobile && (
            <button
              className="bt bt--primary bt--lg mv+"
              disabled={isAbsent(this.state.selected)}
              onClick={this.handleCtaClick}>
              {mobileCtaCopy}
            </button>
          )}
        </div>
      )
    );
  }
}

CardSelectionUnwrapped.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.func), PropTypes.objectOf(PropTypes.func)]),
  defaultSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isMobile: PropTypes.bool.isRequired,
  mobileCtaCopy: PropTypes.node.isRequired,
  onCardSelected: PropTypes.func.isRequired,
};

export const CardSelection = connect(mapStateToProps)(CardSelectionUnwrapped);
